import type { StorefrontProduct } from '../api/client';

// Whether a variant can be ordered, as Shopify answers it: one whose stock is not counted, or that sells past
// zero, can always be ordered, up to the cart's own limit; any other while enough is left. Every storefront
// check reads these, never the raw quantity, which is meaningless for a variant nobody counts.

/** One variant of a storefront product. */
type Variant = StorefrontProduct['variants'][number];

/** The most of one variant a cart line may hold. */
export const MAX_LINE_QUANTITY = 99;

// Reports whether a variant's stock is counted, so that running out stops it selling.
export const stockCounted = (variant: Variant): boolean => variant.trackInventory !== false && !variant.allowBackorders;

// Gives the most of a variant one order may take: its stock when counted, else the cart's own limit.
export function maxQuantity(variant: Variant | undefined): number {
  if (!variant) return 0;
  return stockCounted(variant) ? Math.min(MAX_LINE_QUANTITY, Math.max(0, variant.inventoryQuantity)) : MAX_LINE_QUANTITY;
}

// Reports whether this many of a variant can be ordered now.
export const orderable = (variant: Variant | undefined, quantity = 1): boolean => maxQuantity(variant) >= quantity;

// Gives the stock a product page states: the count when it is kept, nothing when stock is not counted.
export const statedStock = (variant: Variant | undefined): number | null =>
  (variant && stockCounted(variant) ? variant.inventoryQuantity : null);

// Gives a product's stated stock: its variants' counts added up, or nothing when any of them is not counted.
export const productStock = (product: StorefrontProduct): number | null =>
  (product.variants.some((variant) => !stockCounted(variant)) ? null : product.variants.reduce((total, variant) => total + Math.max(0, variant.inventoryQuantity), 0));
